"use client";

import React, { useState, useEffect } from 'react';
import Sidebar from '@/components/Sidebar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { JobCard } from '@/components/JobCard';
import JobFilterForm from '@/components/JobFilterForm';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrentUser } from '@/hooks/use-current-user';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/EmptyState';
import { Package, CheckCircle2 } from 'lucide-react';
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

export default function DriverMyJobsPage() {
  const user = useCurrentUser();
  const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active');
  const [jobs, setJobs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const ITEMS_PER_PAGE = 5;
  const [activePageSize, setActivePageSize] = useState(ITEMS_PER_PAGE);
  const [completedPageSize, setCompletedPageSize] = useState(ITEMS_PER_PAGE);
  const [isFiltersSheetOpen, setFiltersSheetOpen] = useState(false);
  const [density, setDensity] = useState<'comfortable' | 'compact'>('comfortable');

  useEffect(() => {
    if (!user?.id) return;
    setIsLoading(true);
    fetch(`/api/jobs?driverJobs=true&driverId=${user.id}`)
      .then((res) => res.json())
      .then((data) => setJobs(data.jobs))
      .finally(() => setIsLoading(false));
  }, [user?.id]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <main className="flex-1 p-6 md:p-10 ml-0 md:ml-64">
          <div className="max-w-5xl mx-auto">
            <Skeleton className="h-10 w-48 mb-6" />
            <div className="flex space-x-4 mb-6">
              <Skeleton className="h-8 w-32 rounded-full" />
              <Skeleton className="h-8 w-32 rounded-full" />
            </div>
            <div className="space-y-6">
              {Array.from({ length: 3 }).map((_, idx) => (
                <Skeleton key={idx} className="h-48 w-full" />
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  const activeJobsList = jobs.filter((job) => job.status !== 'COMPLETED');
  const completedJobsList = jobs.filter((job) => job.status === 'COMPLETED');
  const visibleActiveJobs = activeJobsList.slice(0, activePageSize);
  const visibleCompletedJobs = completedJobsList.slice(0, completedPageSize);

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 p-6 md:p-10 ml-0 md:ml-64">
        <div className="max-w-5xl mx-auto">
          <header className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold">My Jobs</h1>
              <p className="text-foreground mt-1">
                Manage your accepted delivery jobs
              </p>
            </div>
            <div className="hidden md:flex items-center rounded-md border p-1 ml-auto">
              <Button variant={density === 'comfortable' ? 'default' : 'ghost'} size="sm" onClick={() => setDensity('comfortable')}>Comfortable</Button>
              <Button variant={density === 'compact' ? 'default' : 'ghost'} size="sm" onClick={() => setDensity('compact')}>Compact</Button>
            </div>
          </header>
          <div className="mb-6">
            <div className="hidden md:block">
              <JobFilterForm />
            </div>
            <div className="md:hidden mb-4">
              <Sheet open={isFiltersSheetOpen} onOpenChange={setFiltersSheetOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="w-full">
                    Filters & Sort
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-[75vh] pb-6">
                  <SheetHeader>
                    <SheetTitle>Filters & Sort</SheetTitle>
                  </SheetHeader>
                  <JobFilterForm />
                </SheetContent>
              </Sheet>
            </div>
          </div>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="active" className="relative">
                Active Jobs
                {activeJobsList.length > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary text-[10px] text-primary-foreground">
                    {activeJobsList.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="completed" className="relative text-foreground">
                Completed Jobs
              {completedJobsList.length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center h-5 w-5 rounded-full bg-muted text-[10px] text-muted-foreground">
                  {completedJobsList.length}
                </span>
              )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active" className={`space-y-4 ${density === 'compact' ? 'text-xs' : 'text-sm'}`}>
              {visibleActiveJobs.length === 0 ? (
                <EmptyState
                  icon={<Package className="h-16 w-16 text-muted-foreground" />}
                  title="No active jobs"
                  description="You don’t have any active delivery jobs at the moment."
                  actionLabel="Browse jobs"
                  actionHref="/jobs"
                />
              ) : (
                visibleActiveJobs.map((job) => (
                  <JobCard key={job.id} job={job} variant="active" />
                ))
              )}
              {activePageSize < activeJobsList.length && (
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    onClick={() => setActivePageSize((prev) => prev + ITEMS_PER_PAGE)}
                  >
                    Load more
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="completed" className={`space-y-4 ${density === 'compact' ? 'text-xs' : 'text-sm'}`}>
              {visibleCompletedJobs.length === 0 ? (
                <EmptyState
                  icon={<CheckCircle2 className="h-16 w-16 text-muted-foreground" />}
                  title="No completed jobs yet"
                  description="You haven’t completed any jobs yet."
                  actionLabel="Browse jobs"
                  actionHref="/jobs"
                />
              ) : (
                visibleCompletedJobs.map((job) => (
                  <JobCard key={job.id} job={job} variant="completed" />
                ))
              )}
              {completedPageSize < completedJobsList.length && (
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    onClick={() => setCompletedPageSize((prev) => prev + ITEMS_PER_PAGE)}
                  >
                    Load more
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}